/**
 * Lightweight typed wrapper around `localStorage` for the wallet's session
 * + locally-remembered wallet IDs. The api edge does not (yet) expose
 * list-wallets or list-sub-wallets endpoints; until it does, the wallet
 * caches each id the user has interacted with so it survives a refresh.
 *
 * Storage is split into two narrow stores so the auth flow can clear just
 * the session on logout without dropping the user's saved wallet list.
 *
 * IDs cached here are NOT trusted — every server call still resolves the
 * id against the active session.
 */

const SESSION_KEY = "ggcoin.session";
const WALLETS_KEY = "ggcoin.wallets";

/** Subset of `LoginResponse` we hold between page loads. */
export interface PersistedSession {
  jwt: string;
  accountId: string;
  sessionId: string;
  expiresAt: string;
}

/** What the user has typed in or created during this account's lifetime. */
export interface KnownWallets {
  /** Last-used master wallet id; the dashboard reads from this one. */
  primaryWalletId: string | null;
  /** Every sub-wallet id the user has created or referenced. */
  subWalletIds: string[];
}

const emptyWallets: KnownWallets = { primaryWalletId: null, subWalletIds: [] };

/** Storage abstraction so SSR / tests can swap in a Map-backed fake. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** In-memory fallback for tests + the (very short) SSR window. */
export class MemoryStorage implements StorageLike {
  private readonly map = new Map<string, string>();
  getItem(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
}

/** Resolve a real `localStorage` when one is available; memory otherwise. */
export function defaultStorage(): StorageLike {
  if (typeof globalThis !== "undefined" && "localStorage" in globalThis) {
    return globalThis.localStorage;
  }
  return new MemoryStorage();
}

export class WalletStore {
  constructor(private readonly storage: StorageLike = defaultStorage()) {}

  // ---- session ----

  readSession(): PersistedSession | null {
    const raw = this.storage.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as PersistedSession;
      if (!parsed?.jwt || !parsed.accountId || !parsed.sessionId) return null;
      // Expiry is server-stamped ISO 8601; treat a past or unparseable
      // timestamp as no session so a corrupted blob doesn't read as live.
      const expiresAtMs = new Date(parsed.expiresAt).getTime();
      if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  writeSession(session: PersistedSession): void {
    this.storage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  clearSession(): void {
    this.storage.removeItem(SESSION_KEY);
  }

  // ---- known wallets ----

  readWallets(): KnownWallets {
    const raw = this.storage.getItem(WALLETS_KEY);
    if (!raw) return { ...emptyWallets };
    try {
      const parsed = JSON.parse(raw) as KnownWallets;
      return {
        primaryWalletId: parsed.primaryWalletId ?? null,
        subWalletIds: Array.isArray(parsed.subWalletIds) ? parsed.subWalletIds : [],
      };
    } catch {
      return { ...emptyWallets };
    }
  }

  writeWallets(value: KnownWallets): void {
    this.storage.setItem(WALLETS_KEY, JSON.stringify(value));
  }

  setPrimaryWallet(walletId: string): void {
    const current = this.readWallets();
    if (current.primaryWalletId === walletId) return;
    this.writeWallets({ ...current, primaryWalletId: walletId });
  }

  rememberSubWallet(subWalletId: string): void {
    const current = this.readWallets();
    if (current.subWalletIds.includes(subWalletId)) return;
    this.writeWallets({
      ...current,
      subWalletIds: [...current.subWalletIds, subWalletId],
    });
  }

  forgetSubWallet(subWalletId: string): void {
    const current = this.readWallets();
    const next = current.subWalletIds.filter((id) => id !== subWalletId);
    if (next.length === current.subWalletIds.length) return;
    this.writeWallets({ ...current, subWalletIds: next });
  }

  clearWallets(): void {
    this.storage.removeItem(WALLETS_KEY);
  }
}
